import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { CalendarDays, Clock, CogIcon, MapPin, Share2Icon, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface NPO {
  website: string;
  logo_url: string | null;
  org_name: string;
  description: string;
}

export interface CommunityEvent {
  id: number;
  npo_id: string;
  title: string;
  description: string;
  event_date: string;
  start_time: string;
  duration: number;
  format: string;
  format_details: string;
  max_participants: number;
  prerequisites: string[];
  deadline: string;
  sdgs: string[];
  beneficiary_types: string[];
  event_type: string;
  is_active: boolean;
  images: string[];
  created_at: string;
  updated_at: string;
  slug: string;
  npo: NPO;
  participants_count: Array<{count: number}>;
  participants: string[];
}

interface CommunityEventsResponse {
  data: CommunityEvent[];
}

interface CommunityEventResponse {
  data: CommunityEvent;
}

export default function CommunityEvents({ session, profile }: { session: any; profile: any }) {
  const { data: events, isLoading } = useQuery<CommunityEventsResponse>({
    queryKey: ["communityEvents"],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/campaign/community-events?npoId=${profile?.id}&current_date=false`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch community events");
      }
      return response.json();
    },
    enabled: !!profile?.id,
  });

  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Community Events</h1>
          <Button asChild>
            <Link to="/dashboard/community-events/create">Create Event</Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="w-full">
              <CardHeader>
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full mb-4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const activeEvents = events?.data.filter((event) => event.is_active) || [];
  const inactiveEvents = events?.data.filter((event) => !event.is_active) || [];

  const EventList = ({ events }: { events: CommunityEvent[] }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {events.map((event) => (
        <Card key={event.id} className="flex flex-col overflow-hidden">
          <div className="relative h-40 w-full">
            {event.images?.[0] ? (
              <img
                src={event.images[0]}
                alt={event.title}
                className="object-cover w-full h-full"
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <CalendarDays className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            <Badge 
              variant={event.is_active ? "default" : "secondary"}
              className="absolute top-2 right-2"
            >
              {event.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <CardHeader className="p-4">
            <div className="space-y-1">
              <CardTitle className="text-lg line-clamp-1">{event.title}</CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {event.npo.org_name}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex-1">
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
              {event.description}
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <span>{format(new Date(event.event_date), "MMM d, yyyy")}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>
                  {format(new Date(`2000-01-01T${event.start_time}`), "h:mm a")}
                  {" • "}{event.duration}m
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{event.format}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span>{event.participants_count[0]?.count || 0} of {event.max_participants} spots filled</span>
                  </div>
                  <Progress 
                    value={((event.participants_count[0]?.count || 0) / event.max_participants) * 100} 
                    className="h-2 bg-secondary/30"
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {event.sdgs?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {event.sdgs.slice(0, 3).map((sdg) => (
                    <Badge key={sdg} variant="secondary" className="text-xs">
                      {sdg}
                    </Badge>
                  ))}
                  {event.sdgs.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{event.sdgs.length - 3} more
                    </Badge>
                  )}
                </div>
              )}
              {event.beneficiary_types?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {event.beneficiary_types.slice(0, 2).map((type) => (
                    <Badge key={type} variant="outline" className="text-xs">
                      {type}
                    </Badge>
                  ))}
                  {event.beneficiary_types.length > 2 && (
                    <Badge variant="outline" className="text-xs">
                      +{event.beneficiary_types.length - 2} more
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="p-4 pt-0 flex gap-2">
            <Button asChild variant="outline" size="sm" className="flex-1">
              <Link to={`/dashboard/community-events/${event.slug}`}>View Details</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="flex-1">
              <Link to={`/dashboard/community-events/${event.slug}/edit`}>
                Edit
              </Link>
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Community Events</h1>
        <div className="flex items-center gap-4">
          {profile?.domains?.domain_suffix && <Button variant="link" asChild>
            <a 
              href={`https://${profile.domains?.domain_suffix}.causeconnect.ai/community-events`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <Share2Icon className="h-4 w-4" />
              View in your sharable link
            </a>
          </Button>}
          {!profile?.domains?.domain_suffix && (
            <Button variant="destructive" size="sm" className="ml-4" onClick={() => navigate('/dashboard/mission-hub/setup')}>
              <CogIcon className="h-4 w-4 mr-2" />
              Setup Mission Hub to Start Sharing
            </Button>
          )}
          <Button asChild>
            <Link to="/dashboard/community-events/create">Create Event</Link>
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="active" className="space-y-6">
        <TabsList>
          <TabsTrigger value="active">Active Events ({activeEvents.length})</TabsTrigger>
          <TabsTrigger value="inactive">Inactive Events ({inactiveEvents.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="active">
          {activeEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No active events found</p>
            </div>
          ) : (
            <EventList events={activeEvents} />
          )}
        </TabsContent>
        
        <TabsContent value="inactive">
          {inactiveEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No inactive events found</p>
            </div>
          ) : (
            <EventList events={inactiveEvents} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
